/**
 * A short preview of a question, for the list on the results screen.
 *
 * Two things it has to get right.
 *
 * The question is HTML, so the text has to be taken out properly. Stripping
 * tags with a regular expression leaves entities behind, which is why
 * previews used to show a literal &amp; in the middle of a formula.
 *
 * Cutting the text at a fixed length can land inside a maths expression. That
 * leaves the opening $ or $$ without its closing partner, MathJax cannot
 * parse it, and the reader sees the LaTeX source. Three of the twenty
 * previews on TMUA Mock 3 Paper 1 did exactly that. So the cut moves back to
 * before the expression starts rather than through it.
 */

const MATHS = /\$\$[\s\S]*?\$\$|\$[^$]*\$/g;

export function plainText(html) {
  if (!html) return '';
  let text;
  try {
    text = new DOMParser().parseFromString(String(html), 'text/html').body.textContent || '';
  } catch (e) {
    // No DOM available. Falls back to stripping tags, which is worse but
    // still better than showing markup.
    text = String(html).replace(/<[^>]+>/g, '');
  }
  return text.replace(/\s+/g, ' ').trim();
}

export function questionPreview(html, limit = 100) {
  const text = plainText(html);
  if (!text) return 'Question text';
  if (text.length <= limit) return text;

  const spans = [];
  let m;
  MATHS.lastIndex = 0;
  while ((m = MATHS.exec(text)) !== null) {
    spans.push([m.index, m.index + m[0].length]);
  }

  let cut = limit;
  for (const [start, end] of spans) {
    if (cut > start && cut < end) {
      // Cutting here would split the expression. Prefer to stop before it.
      // If that leaves almost nothing, show the whole expression instead,
      // so the preview still says something.
      cut = start < limit / 3 ? end : start;
      break;
    }
  }

  const shown = text.slice(0, cut).trimEnd();
  return shown.length < text.length ? `${shown}...` : shown;
}

export default questionPreview;
